import CustomAuthenticator from "@/components/CustomAuthenticator/CustomAuthenticator";
import LoadingScreen from "@/components/LoadingScreen/LoadingScreen";
import NewVersionModal from "@/components/NewVersionModal/NewVersionModal";
import WelcomeScreen from "@/components/WelcomeScreen/WelcomeScreen";
import useAnalytics from "@/hooks/useAnalytics";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import useIntercom from "@/hooks/useIntercom";
import { useUserSettings } from "@/hooks/useUserSettings";
import { trackedRoles } from "@/providers/AnalyticsProvider";
import { UserTypeProvider } from "@/providers/UserTypeProvider";
import YearProvider from "@/providers/YearProvider";
import { WithAuthenticatorProps } from "@aws-amplify/ui-react";
import { Layout } from "antd";
import { Content } from "antd/es/layout/layout";
import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import ProductDrawer from "../drawers/ProductDrawer/ProductDrawer";
import Topbar from "./Header/Topbar";
import styles from "./Layout.module.scss";
import RootMenu from "./RootMenu/RootMenu";

const PrivateRouteLayout = ({ user }: WithAuthenticatorProps) => {
  const LayoutComponent = () => {
    const {
      data: userSettings,
      isError: userError,
      isLoading: userSettingsIsLoading,
    } = useUserSettings();
    const userId = userSettings?.user_id;
    const role = userSettings?.role;
    const companyId = userSettings?.company_id;

    const {
      data: companySettings,
      isError: companyError,
      isLoading: companySettingsIsLoading,
    } = useCompanySettings();
    const companyName = companySettings?.company_name;

    const { launch } = useIntercom();

    /* identify analytics */
    const analytics = useAnalytics();
    useEffect(() => {
      if (userId && companyId && companyName && role && trackedRoles.includes(role)) {
        analytics.identify(companyId, companyName, userId);
      }
    }, [userId, companyId, companyName, role]);

    /* launch intercom */
    useEffect(() => {
      if (userId) {
        launch();
      }
    }, [userId]);

    if (userSettingsIsLoading || companySettingsIsLoading) {
      return <TopBarProgress />;
    } else if (userError || companyError) {
      return (
        <LoadingScreen title="Something went wrong">
          <>
            Please try refreshing. If the problem persists, send us a message and we'll help you out
            as soon as we can. Sorry for the inconvenience.
          </>
        </LoadingScreen>
      );
    } else if (companySettings?.in_maintenance) {
      return (
        <LoadingScreen title="We're down for maintenance">
          <>This will take about an hour. Sorry for the inconvenience.</>
        </LoadingScreen>
      );
    } else if (companySettings?.show_onboarding) {
      return <WelcomeScreen />;
    } else {
      return (
        <>
          <NewVersionModal />
          <Layout className={styles.Layout}>
            <Topbar />
            <RootMenu />
            <Content className={styles.content}>
              <Outlet />
            </Content>
          </Layout>
          <ProductDrawer />
        </>
      );
    }
  };

  return (
    <CustomAuthenticator>
      <YearProvider>
        <UserTypeProvider type={"internal"}>
          <LayoutComponent />
        </UserTypeProvider>
      </YearProvider>
    </CustomAuthenticator>
  );
};

export default PrivateRouteLayout;
